import React from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    FiHeart,
    FiShoppingCart,
    FiTrash2,
    FiShield,
    FiCheckCircle,
    FiShoppingBag,
    FiArrowRight,
    FiAward,
} from 'react-icons/fi';
import { useShop } from '../context/ShopContext';
import { useDiscount } from '../context/DiscountContext';
import { getImageUrl } from '../utils/imageUrl';
import {
    cashPriceFromMrp,
    getProductDiscount,
    formatPrice,
} from '../utils/cashPricing';
import './WishlistPage.css';

const WishlistPage = () => {
    const { wishlist, removeFromWishlist, addToCart } = useShop();
    const { effectiveDiscount } = useDiscount();

    return (
        <div className="wishlist-page">
            {/* Showroom Header */}
            <header className="wishlist-hero-header">
                <div className="container">
                    <div className="wishlist-hero-content">
                        <div className="wishlist-hero-badge">
                            <FiHeart /> Showroom Favourites
                        </div>
                        <h1 className="wishlist-hero-title">
                            Saved Wishlist
                            {wishlist.length > 0 && (
                                <span className="wishlist-count-pill">
                                    {wishlist.length} {wishlist.length === 1 ? 'item' : 'items'}
                                </span>
                            )}
                        </h1>
                        <p className="wishlist-hero-subtitle">
                            Products you have saved for later. When you decide to buy, enjoy{' '}
                            <strong>Instant Cash Discounts</strong> or apply for easy monthly installments.
                        </p>
                    </div>
                </div>
            </header>

            <main className="container wishlist-page-inner">
                {wishlist.length === 0 ? (
                    <div className="wishlist-empty-luxury">
                        <div className="wishlist-empty-icon-wrap">
                            <FiHeart className="wishlist-empty-icon" />
                        </div>
                        <h2>Your Wishlist is Empty</h2>
                        <p>
                            Explore our showroom catalog and tap the heart icon on any air conditioner,
                            refrigerator, or home appliance to save it here for fast access.
                        </p>
                        <Link to="/products" className="wishlist-btn-primary">
                            <FiShoppingBag /> Browse Showroom Catalog
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="wishlist-toolbar-bar">
                            <span className="wishlist-toolbar-count">
                                Saved Items (<strong>{wishlist.length}</strong>)
                            </span>
                            <span className="wishlist-toolbar-note">
                                <FiCheckCircle /> Prices include Showroom Instant Cash Discounts
                            </span>
                        </div>

                        <div className="wishlist-cards-grid">
                            {wishlist.map((item, index) => {
                                const img = getImageUrl(item.image);
                                const mrp = Number(item.mrp ?? item.cash_price ?? 0) || 0;
                                const itemDiscount = getProductDiscount(item, effectiveDiscount);
                                const cashPrice = cashPriceFromMrp(mrp, itemDiscount);
                                const savings = Math.max(0, mrp - cashPrice);

                                return (
                                    <article
                                        key={item.id}
                                        className="wishlist-luxury-card"
                                        style={{ animationDelay: `${index * 50}ms` }}
                                    >
                                        {/* Image Section */}
                                        <div className="wishlist-card-image-box">
                                            <Link
                                                to={`/products/${item.id}`}
                                                className="wishlist-card-image-anchor"
                                            >
                                                {img ? (
                                                    <img
                                                        src={img}
                                                        alt={item.name}
                                                        loading="lazy"
                                                        className="wishlist-card-img"
                                                    />
                                                ) : (
                                                    <div className="wishlist-card-img-placeholder">
                                                        No Image
                                                    </div>
                                                )}
                                            </Link>

                                            {itemDiscount > 0 && (
                                                <span className="wishlist-card-discount-badge">
                                                    -{itemDiscount}% Cash
                                                </span>
                                            )}

                                            <button
                                                type="button"
                                                className="wishlist-card-quick-remove"
                                                onClick={() => {
                                                    removeFromWishlist(item.id);
                                                    toast.success('Removed from wishlist');
                                                }}
                                                title="Remove from saved items"
                                                aria-label={`Remove ${item.name}`}
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>

                                        {/* Body details */}
                                        <div className="wishlist-card-content">
                                            <div className="wishlist-card-tags-row">
                                                <span className="wishlist-card-brand">
                                                    <FiAward /> {item.brand || 'Rahber'}
                                                </span>
                                                <span className="wishlist-card-stock">
                                                    In Stock
                                                </span>
                                            </div>

                                            <Link
                                                to={`/products/${item.id}`}
                                                className="wishlist-card-title-link"
                                                title={item.name}
                                            >
                                                {item.name}
                                            </Link>

                                            {/* Pricing Box */}
                                            <div className="wishlist-card-price-container">
                                                <div className="wishlist-card-price-primary">
                                                    <strong className="wishlist-cash-amount">
                                                        {formatPrice(cashPrice)}
                                                    </strong>
                                                    <span className="wishlist-cash-label">Cash Price</span>
                                                </div>

                                                <div className="wishlist-card-mrp-reference">
                                                    <span className="wishlist-mrp-val">
                                                        MRP: {formatPrice(mrp)}
                                                    </span>
                                                    {savings > 0 && (
                                                        <span className="wishlist-save-tag">
                                                            Save {formatPrice(savings)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="wishlist-card-action-buttons">
                                                <button
                                                    type="button"
                                                    className="wishlist-btn-add-cart"
                                                    onClick={() => {
                                                        addToCart(item, 1);
                                                        const discountMsg = itemDiscount > 0 ? ` (${itemDiscount}% off MRP)` : '';
                                                        toast.success(
                                                            `Added to cart at cash price${discountMsg}`
                                                        );
                                                    }}
                                                >
                                                    <FiShoppingCart /> Add to Cart
                                                </button>

                                                <Link
                                                    to={`/products/${item.id}`}
                                                    className="wishlist-btn-view-details"
                                                    title="View product details"
                                                >
                                                    <FiArrowRight />
                                                </Link>
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        {/* High-trust footer */}
                        <div className="wishlist-trust-footer">
                            <div className="wishlist-trust-cell">
                                <FiShield className="trust-icon" />
                                <div>
                                    <strong>Genuine Brand Sealed</strong>
                                    <p>Direct from official authorized manufacturer distributors</p>
                                </div>
                            </div>
                            <div className="wishlist-trust-cell">
                                <FiCheckCircle className="trust-icon" />
                                <div>
                                    <strong>Official Warranty</strong>
                                    <p>Claimable across all Pakistan authorized warranty centers</p>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};

export default WishlistPage;
